import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { MapPin, Truck, Car, RefreshCw, Fuel } from "lucide-react";
import { Reveal, easeOutExpo } from "../lib/anim";

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNERS = [
  { icon: MapPin, label: "Global Mapping" },
  { icon: Truck, label: "Logistics" },
  { icon: Car, label: "Automotive" },
];

// Animated integer counter: eases from 0 -> target on mount, then adds tiny
// increments every few seconds so the number feels alive.
function LiveCounter({ target, prefix = "", suffix = "", decimals = 0, tickEvery = 3000, tickMin = 1, tickMax = 6, format = "en-IN" }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  const currentRef = useRef(0);

  useEffect(() => {
    const controls = animate(mv, target, { duration: 2.5, ease: [0.16, 1, 0.3, 1] });
    currentRef.current = target;
    return () => controls.stop();
  }, [mv, target]);

  useEffect(() => {
    const unsub = mv.on("change", (v) => {
      const n = Number(v);
      setDisplay(
        prefix +
          n.toLocaleString(format, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) +
          suffix
      );
    });
    return unsub;
  }, [mv, prefix, suffix, decimals, format]);

  // Gentle live ticks after the initial animation resolves
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => {
      const bump = tickMin + Math.random() * (tickMax - tickMin);
      currentRef.current += bump;
      animate(mv, currentRef.current, { duration: 1.2, ease: "easeOut" });
    }, tickEvery);
    return () => clearInterval(id);
  }, [mv, target, tickEvery, tickMin, tickMax]);

  return <>{display}</>;
}

export const WhyWePay = () => {
  const [stats, setStats] = useState({ total_km_mapped: 0, total_paid_inr: 0, active_drivers: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/stats`);
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch {
        // baseline sentinel so counters still ramp
        if (!cancelled) setStats({ total_km_mapped: 1200, total_paid_inr: 8500, active_drivers: 42 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="why-we-pay"
      data-testid="why-we-pay-section"
      className="relative py-24 md:py-32 bg-black border-y border-white/10 overflow-hidden"
    >
      {/* Ambient glows */}
      <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[#00B4D8]/10 blur-[130px]" />
      <div className="absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full bg-[#4CAF50]/10 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center">
        {/* Left copy */}
        <div>
          <Reveal>
            <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4">
              / Why We Pay
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
              You provide the data.{" "}
              <span className="gx-text-gradient">We fund your journey.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-7 max-w-xl text-slate-300 leading-relaxed">
              Geonetix bridges the gap between everyday drivers and the global
              industries that rely on precise, up-to-date spatial data. By
              partnering directly with global mapping companies, logistics
              giants, and the automobile industry, Geonetix crowdsources
              real-time road insights to power the navigation systems of
              tomorrow.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-5 max-w-xl text-slate-300 leading-relaxed">
              Crucially, Geonetix operates its own proprietary{" "}
              <span className="text-[#00B4D8] font-semibold">Geomaps</span> —
              your daily mapping contributions directly update this platform
              every 24 hours to create a highly accurate, future-proof mapping
              ecosystem. Because this vital data relies entirely on your
              real-world miles, Geonetix reinvests commercial profits back into
              the community, covering your fuel expenses for every valid map
              submission.
            </p>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {PARTNERS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.label}
                    data-testid={`why-partner-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-xs font-semibold text-slate-200"
                  >
                    <Icon size={13} className="text-[#00B4D8]" />
                    {p.label}
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.45}>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="rounded-2xl border border-[#00B4D8]/30 bg-[#0f2b4a]/50 p-5">
                <div className="flex items-center gap-2 text-[#00B4D8] mb-2">
                  <RefreshCw size={14} />
                  <span className="font-mono-gx text-[10px] uppercase tracking-widest">
                    Geomaps refresh
                  </span>
                </div>
                <div className="font-display font-black text-2xl tracking-tight">
                  Every 24h
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Driven by your submissions
                </p>
              </div>
              <div className="rounded-2xl border border-[#4CAF50]/30 bg-[#0f2b1a]/50 p-5">
                <div className="flex items-center gap-2 text-[#4CAF50] mb-2">
                  <Fuel size={14} />
                  <span className="font-mono-gx text-[10px] uppercase tracking-widest">
                    Payout in
                  </span>
                </div>
                <div className="font-display font-black text-2xl tracking-tight">
                  Fuel &amp; INR
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  For every valid submission
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: easeOutExpo }}
          className="relative"
        >
          {/* Framed image */}
          <div className="relative rounded-3xl border border-white/10 bg-[#050d1a] p-2 overflow-hidden gx-green-glow">
            <div className="relative rounded-2xl overflow-hidden">
              <picture>
                <source srcSet="/why-we-pay-map.webp" type="image/webp" />
                <img
                  src="/why-we-pay-map.png"
                  alt="Live map of GeoNetix drivers contributing hyper-local street data"
                  data-testid="why-we-pay-image"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto block"
                />
              </picture>
              {/* Vignette + top-bottom fades to blend with the black theme */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#00B4D8]/5 to-[#4CAF50]/5 mix-blend-overlay" />

              {/* Floating live counters */}
              <motion.div
                data-testid="counter-km-mapped"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute bottom-4 left-4 gx-glass border border-[#4CAF50]/40 rounded-2xl px-4 py-3"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#4CAF50] font-mono-gx mb-0.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4CAF50] opacity-70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4CAF50]" />
                  </span>
                  Live · KM mapped
                </div>
                <div className="text-lg font-black tracking-tight font-mono-gx text-white">
                  <LiveCounter target={stats.total_km_mapped} suffix=" km" tickEvery={4000} tickMin={0.4} tickMax={1.8} />
                </div>
              </motion.div>
              <motion.div
                data-testid="counter-paid-inr"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute top-4 right-4 gx-glass border border-[#00B4D8]/40 rounded-2xl px-4 py-3"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#00B4D8] font-mono-gx mb-0.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00B4D8] opacity-70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00B4D8]" />
                  </span>
                  Live · Paid out
                </div>
                <div className="text-lg font-black tracking-tight font-mono-gx text-white">
                  <LiveCounter target={stats.total_paid_inr} prefix="₹" tickEvery={5000} tickMin={1} tickMax={9} />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Decorative dashed frame */}
          <div className="absolute -inset-3 rounded-[28px] border border-dashed border-[#00B4D8]/20 pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
};
